import { useState, useEffect, useRef } from 'react';
import { Play, X, Quotes } from '@phosphor-icons/react';
import type { VideoStoryDto } from '@pms/shared';
import { HOTELIER_STORIES, type HotelierStory } from '../../data/landing';
import { useVideoStories } from '../../hooks/useVideoStories';
import { Reveal } from '../../lib/motion';

/** Map a curated video-story DTO onto the card shape (field names align). */
function toStory(s: VideoStoryDto): HotelierStory {
  return {
    id: s.id,
    name: s.name,
    hotel: s.role ?? '',
    location: '',
    quote: s.quote ?? '',
    posterUrl: s.posterUrl ?? '',
    videoUrl: s.videoUrl,
  };
}

/**
 * Hotelier video stories — the supply-side social proof the client asked for.
 * Poster cards with a play affordance open a modal <video> player. Reads the
 * super-admin-curated feed (GET /v1/public/video-stories) and falls back to the
 * static `HOTELIER_STORIES` when the feed is empty or unreachable, so the band
 * never collapses (R5).
 */
export function HotelierStories() {
  const [active, setActive] = useState<HotelierStory | null>(null);
  const { data } = useVideoStories();
  const stories =
    data && data.length > 0 ? data.map(toStory) : HOTELIER_STORIES;

  return (
    <section aria-labelledby="stories-h2" className="bg-cream-deep">
      <div className="px-6 py-20 sm:px-10 lg:px-16 lg:py-28 2xl:px-24">
        <h2
          id="stories-h2"
          className="max-w-3xl text-balance font-serif text-[2.4rem] font-black leading-[0.98] tracking-tight text-ink sm:text-5xl xl:whitespace-nowrap"
        >
          Hear it from the <span className="italic text-clay">hosts.</span>
        </h2>
        <p className="mt-4 max-w-xl text-lg leading-relaxed text-ink-soft">
          Independent hoteliers on what changed when they joined BestAcco.
        </p>

        <ul role="list" className="mt-12 grid grid-cols-1 gap-7 md:grid-cols-3">
          {stories.map((s, i) => (
            <Reveal as="li" key={s.id} delay={i * 120} className="h-full">
              <StoryCard story={s} onPlay={() => setActive(s)} />
            </Reveal>
          ))}
        </ul>
      </div>

      {active && <StoryModal story={active} onClose={() => setActive(null)} />}
    </section>
  );
}

function StoryCard({ story, onPlay }: { story: HotelierStory; onPlay: () => void }) {
  const [err, setErr] = useState(false);
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-[1.5rem] border border-sand bg-white">
      <button
        type="button"
        onClick={onPlay}
        className="group relative block aspect-[4/3] w-full overflow-hidden bg-clay-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay focus-visible:ring-inset"
        aria-label={`Play ${story.name}'s story`}
      >
        {!err && (
          <img
            src={story.posterUrl}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setErr(true)}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        )}
        <span className="absolute inset-0 bg-ink/25 transition-colors group-hover:bg-ink/35" aria-hidden="true" />
        <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-cream/95 text-clay shadow-lg transition-transform group-hover:scale-110">
          <Play weight="fill" className="ml-0.5 h-7 w-7 text-clay" aria-hidden="true" />
        </span>
      </button>

      <div className="flex flex-1 flex-col p-6">
        <Quotes className="h-6 w-6 text-clay/40" aria-hidden="true" />
        <blockquote className="mt-3 flex-1 font-serif text-lg leading-relaxed text-ink-soft">
          “{story.quote}”
        </blockquote>
        <div className="mt-5 border-t border-sand pt-4">
          <p className="font-semibold text-ink">{story.name}</p>
          <p className="text-sm text-ink-muted">
            {[story.hotel, story.location].filter(Boolean).join(' · ')}
          </p>
        </div>
      </div>
    </div>
  );
}

function StoryModal({ story, onClose }: { story: HotelierStory; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const [videoErr, setVideoErr] = useState(false);

  useEffect(() => {
    closeRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${story.name}'s story`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-ink shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Close video"
          className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-cream/90 text-ink transition hover:bg-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay"
        >
          <X className="h-5 w-5" />
        </button>
        {videoErr ? (
          <div className="flex aspect-video w-full flex-col items-center justify-center gap-2 bg-black px-6 text-center text-cream">
            <X className="h-8 w-8 text-cream/70" aria-hidden="true" />
            <p className="text-sm font-medium">This video couldn't be loaded.</p>
            <p className="text-xs text-cream/60">Please try again later.</p>
          </div>
        ) : (
          /* eslint-disable-next-line jsx-a11y/media-has-caption */
          <video
            src={story.videoUrl}
            poster={story.posterUrl}
            controls
            autoPlay
            onError={() => setVideoErr(true)}
            className="aspect-video w-full bg-black"
          />
        )}
        <div className="flex items-center justify-between gap-4 px-5 py-4 text-cream">
          <div>
            <p className="font-serif text-lg font-semibold">{story.name}</p>
            <p className="text-sm text-cream/70">
              {[story.hotel, story.location].filter(Boolean).join(' · ')}
            </p>
          </div>
          <span className="text-xs text-cream/50">Sample film · replace with the real story</span>
        </div>
      </div>
    </div>
  );
}
